import axios from "axios";
import { Toast } from "primereact/toast";
import { DragEvent, useEffect, useId, useRef, useState } from "react";
import uploadService, {
  UploadCategory,
} from "../../services/uploadService";
import {
  ActionButton,
  Actions,
  DropHint,
  DropTitle,
  DropZone,
  EmptyState,
  IconBadge,
  Label,
  PreviewFrame,
  PreviewImage,
  ProgressBlock,
  ProgressLabel,
  ProgressTrack,
  ProgressValue,
  UploadContainer,
  UploadingMask,
} from "./styles";

const UploadGlyph = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 16V5m0 0L8 9m4-4 4 4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5 16.5V18a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-1.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const INVALID_URL_ERROR = "A resposta do upload não contém uma URL válida.";
const GENERIC_UPLOAD_ERROR =
  "Não foi possível enviar a imagem. Tente novamente.";

interface ImageUploadProps {
  value: string;
  onChange: (url: string) => void;
  category: UploadCategory;
  contentId?: number | string;
  label?: string;
  required?: boolean;
  onUploadingChange?: (uploading: boolean) => void;
}

const isValidUploadUrl = (value: unknown): value is string => {
  if (typeof value !== "string" || value.trim().length === 0) {
    return false;
  }

  try {
    const url = new URL(value, window.location.origin);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

const getFileValidationError = (file: File): string | undefined => {
  if (!ACCEPTED_TYPES.has(file.type)) {
    return "Selecione uma imagem JPG, JPEG, PNG ou WEBP.";
  }
  if (file.size > MAX_FILE_SIZE) {
    return "A imagem deve ter no máximo 5 MB.";
  }
};

const getUploadErrorMessage = (error: unknown) => {
  if (error instanceof Error && error.message === INVALID_URL_ERROR) {
    return error.message;
  }
  return GENERIC_UPLOAD_ERROR;
};

export const ImageUpload = ({
  value,
  onChange,
  category,
  contentId,
  label = "Imagem",
  required = false,
  onUploadingChange,
}: ImageUploadProps) => {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);
  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);
  const activeRequestRef = useRef<{
    id: number;
    controller: AbortController;
  } | null>(null);
  const onUploadingChangeRef = useRef(onUploadingChange);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    onUploadingChangeRef.current = onUploadingChange;
  }, [onUploadingChange]);

  useEffect(() => {
    const activeRequest = activeRequestRef.current;
    if (!activeRequest) {
      return;
    }

    requestIdRef.current += 1;
    activeRequestRef.current = null;
    activeRequest.controller.abort();
    onUploadingChangeRef.current?.(false);
    setUploading(false);
    setProgress(0);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }, [category, contentId]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      requestIdRef.current += 1;

      const activeRequest = activeRequestRef.current;
      activeRequestRef.current = null;
      if (activeRequest) {
        activeRequest.controller.abort();
        onUploadingChangeRef.current?.(false);
      }
    };
  }, []);

  const showError = (detail: string) =>
    toast.current?.show({ severity: "error", summary: "Erro", detail, life: 4000 });

  const isCurrentRequest = (requestId: number) =>
    mountedRef.current && activeRequestRef.current?.id === requestId;

  const startUpload = () => {
    activeRequestRef.current?.controller.abort();
    const controller = new AbortController();
    const requestId = ++requestIdRef.current;
    activeRequestRef.current = { id: requestId, controller };
    setUploading(true);
    setProgress(0);
    onUploadingChangeRef.current?.(true);
    return { controller, requestId };
  };

  const finishUpload = (requestId: number) => {
    if (activeRequestRef.current?.id !== requestId) {
      return;
    }

    activeRequestRef.current = null;
    onUploadingChangeRef.current?.(false);
    if (mountedRef.current) {
      setUploading(false);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  const handleUploadError = (error: unknown, requestId: number) => {
    if (!isCurrentRequest(requestId) || axios.isCancel(error)) {
      return;
    }
    showError(getUploadErrorMessage(error));
  };

  const upload = async (file?: File) => {
    if (!file) {
      return;
    }

    const validationError = getFileValidationError(file);
    if (validationError) {
      showError(validationError);
      return;
    }

    const { controller, requestId } = startUpload();

    try {
      const response = await uploadService.uploadImage({
        file,
        category,
        contentId,
        signal: controller.signal,
        onProgress: (nextProgress) => {
          if (isCurrentRequest(requestId)) {
            setProgress(nextProgress);
          }
        },
      });

      if (!isCurrentRequest(requestId)) {
        return;
      }

      if (!isValidUploadUrl(response.data?.url)) {
        throw new Error(INVALID_URL_ERROR);
      }

      onChange(response.data.url.trim());
      setProgress(100);
      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: "Imagem enviada com sucesso.",
        life: 3000,
      });
    } catch (error) {
      handleUploadError(error, requestId);
    } finally {
      finishUpload(requestId);
    }
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setDragging(false);
    void upload(event.dataTransfer.files?.[0]);
  };

  return (
    <UploadContainer>
      <Toast ref={toast} />
      {label && (
        <Label htmlFor={inputId}>
          {label}
          {required ? " *" : ""}
        </Label>
      )}
      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
        hidden
        disabled={uploading}
        onChange={(event) => void upload(event.target.files?.[0])}
      />
      <DropZone
        type="button"
        isDragging={dragging}
        hasPreview={Boolean(value)}
        isUploading={uploading}
        onDragEnter={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        disabled={uploading}
      >
        {value ? (
          <PreviewFrame>
            <PreviewImage src={value} alt={`Pré-visualização: ${label || "imagem"}`} />
            {uploading && (
              <UploadingMask>Enviando imagem... {progress}%</UploadingMask>
            )}
          </PreviewFrame>
        ) : (
          <EmptyState>
            <IconBadge>
              <UploadGlyph />
            </IconBadge>
            <DropTitle>
              {uploading ? "Enviando imagem..." : "Arraste a imagem aqui"}
            </DropTitle>
            <DropHint>ou clique para selecionar</DropHint>
            <DropHint>JPG, JPEG, PNG ou WEBP · até 5 MB</DropHint>
          </EmptyState>
        )}
      </DropZone>
      {uploading && (
        <ProgressBlock>
          <ProgressTrack
            role="progressbar"
            aria-label="Progresso do upload"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
          >
            <ProgressValue $progress={progress} />
          </ProgressTrack>
          <ProgressLabel>{progress}%</ProgressLabel>
        </ProgressBlock>
      )}
      {value && (
        <Actions>
          <ActionButton
            type="button"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            Substituir
          </ActionButton>
          <ActionButton
            type="button"
            $danger
            disabled={uploading}
            onClick={() => onChange("")}
          >
            Remover
          </ActionButton>
        </Actions>
      )}
    </UploadContainer>
  );
};
